import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { inFrameDialogProps } from '@/designs-dev/device/inFrameDialog';
import { NEEDS_OPTIONS } from '@/cam-admin/paywall/Shortlist.fixtures';
import { type Shortlists } from '@/cam-admin/paywall/useShortlists';
import { PUBLISH } from '@/cam-admin/paywall/Shortlist.content';

// Publishing, now that publishing is what sends.
//
// Message and Publish were two actions. Collapsing them moved the weight of this
// screen: it used to be a decision about people — a row per provider, ticked or
// not — and it is now a decision about words. Nobody has paid at the moment it
// opens, so there is nobody to hold back; what there is instead is one message
// about somebody's mother, going to every provider in the area at once, and this
// is the last time a person reads it.
//
// So the body is the request, pulled from the care assessment and editable.
// Editable rather than read-only because auto-pulled data is thin or wrong often
// enough that a CAM will spot it, and this is the last moment anybody looks.

const PublishAndSend = ({
  shortlists,
  inFrame,
}: {
  shortlists: Shortlists;
  inFrame?: boolean;
}) => {
  const {
    sendDraft,
    isPublishOpen,
    patchSendDraft,
    cancelPublish,
    openPreview,
    publish,
  } = shortlists;

  if (!isPublishOpen || sendDraft === null) return null;

  return (
    <Dialog
      open
      onClose={cancelPublish}
      maxWidth='md'
      fullWidth
      {...(inFrame ? inFrameDialogProps : {})}
    >
      <DialogTitle>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
        >
          <Typography variant='h3' color='primary.main'>
            {PUBLISH.title}
          </Typography>
          <IconButton aria-label='close' onClick={cancelPublish}>
            <Icon icon='xmark' fontSize={18} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant='subtitle1'>{PUBLISH.intro}</Typography>
          <Stack spacing={3}>
            <TextField
              label={PUBLISH.careType}
              value={sendDraft.careType}
              onChange={(e) => patchSendDraft({ careType: e.target.value })}
              size='small'
              fullWidth
            />

            {/* Care needs: the picker, then what it picked.
            The chips used to be the control — every option on the screen, tap to
            toggle — which read as a row of buttons a CAM had to audit rather than
            a value they had chosen. Now the dropdown is where choosing happens and
            the chips are the answer: only the selected needs are there, and each
            carries an x, so a chip is either information or a way to remove
            itself, never a thing you press to turn on.
            The menu renders with the portal off. Inside the laptop frame the
            dialog is in the local DOM, so a portalled menu paints behind the very
            dialog it belongs to. */}
            <Stack spacing={1.5}>
              <TextField
                slotProps={{
                  select: {
                    multiple: true,
                    displayEmpty: true,
                    MenuProps: { disablePortal: true },
                    // A count rather than the names: the chips underneath are already
                    // the list, and reading it twice on one row of the form is the
                    // duplication the chips were moved out of the field to avoid.
                    renderValue: (val) => {
                      const chosen = val as string[];
                      return (
                        <Typography
                          variant='body2'
                          color={
                            chosen.length ? 'text.primary' : 'text.secondary'
                          }
                        >
                          {PUBLISH.needsSummary(chosen.length)}
                        </Typography>
                      );
                    },
                  },
                }}
                select
                label={PUBLISH.needs}
                value={sendDraft.needs}
                onChange={(e) =>
                  patchSendDraft({
                    needs: e.target.value as unknown as string[],
                  })
                }
                size='small'
                fullWidth
              >
                {NEEDS_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>

              {sendDraft.needs.length > 0 && (
                <Stack sx={{ flexWrap: 'wrap', gap: 1 }} direction='row'>
                  {sendDraft.needs.map((o) => (
                    <Chip
                      key={o}
                      label={o}
                      onDelete={() =>
                        patchSendDraft({
                          needs: sendDraft.needs.filter((n) => n !== o),
                        })
                      }
                      variant='filled'
                      color='primary'
                    />
                  ))}
                </Stack>
              )}
            </Stack>

            {/* Funding and area on one line. Neither is a sentence, both are short,
            and a provider reads them as one answer about whether this lead is
            theirs to take. */}
            <Stack direction='row' spacing={2}>
              <TextField
                label={PUBLISH.funding}
                value={sendDraft.funding}
                onChange={(e) => patchSendDraft({ funding: e.target.value })}
                size='small'
                fullWidth
              />
              <TextField
                label={PUBLISH.area}
                value={sendDraft.area}
                onChange={(e) => patchSendDraft({ area: e.target.value })}
                size='small'
                fullWidth
              />
            </Stack>

            {/* Last, and the only box the assessment did not fill. Everything above
            came from the care circle's record; this is the part only the person
            who took the call knows. */}
            <TextField
              label={PUBLISH.extra}
              placeholder={PUBLISH.extraPlaceholder}
              value={sendDraft.extra}
              onChange={(e) => patchSendDraft({ extra: e.target.value })}
              multiline
              minRows={3}
              size='small'
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>

      {/* Preview sits away from the pair, on the left. It is not a third choice
          about whether to send — it is a way of reading what the other two are
          about, so putting it beside Cancel would make going back and looking
          closer the same shape of act. */}
      <DialogActions>
        <Stack sx={{ flex: 1 }} direction='row' spacing={2}>
          <Button
            ariaLabel={PUBLISH.preview}
            variant='link'
            onPress={openPreview}
          >
            {PUBLISH.preview}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            ariaLabel={PUBLISH.cancel}
            variant='secondary'
            onPress={cancelPublish}
          >
            {PUBLISH.cancel}
          </Button>
          <Button ariaLabel={PUBLISH.cta} variant='primary' onPress={publish}>
            {PUBLISH.cta}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

PublishAndSend.displayName = 'PublishAndSend';
export default PublishAndSend;
